<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <span class="font-bold text-base" v-show="msg">{{ msg }}</span>

    <!-- Step 1: Email -->
    <div v-if="currentStep === 1" class="space-y-4">
      <div class="space-y-2">
        <label for="email" class="block text-sm font-medium text-gray-500">
          Email <span class="text-red-500 text-base font-bold">*</span>
        </label>
        <div class="relative">
          <Mail class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"/>
          <input
              id="email"
              type="text"
              ref="emailInputRef"
              placeholder="you@example.com"
              :class="[
              'pl-10 h-12 text-base w-full rounded-md border-input bg-background py-2 px-3 border-2 focus:border-blue-500 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-blue-100 focus:outline-none',
              errors.email && 'border-red-500 focus:ring-red-500'
            ]"
              v-model="email"
              @input="validateEmail(email)"
          />
        </div>
        <p v-if="errors.email" class="text-red-500 text-base mt-1 font-semibold leading-5">{{ errors.email }}</p>
      </div>

      <button
          type="submit"
          class="w-full h-12 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:pointer-events-none"
          :disabled="isLoading"
      >
        {{ isLoading ? "Đang xác thực..." : "Tiếp tục" }}
      </button>
    </div>

    <!-- Step 2: Phone Number -->
    <div v-if="currentStep === 2" class="space-y-4">
      <div class="space-y-2">
        <label for="phone" class="block text-base font-medium text-gray-500">
          Số điện thoại <span class="text-red-500 text-base font-bold">*</span>
        </label>
        <div class="relative">
          <Phone class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"/>
          <input
              id="phone"
              type="tel"
              ref="phoneInputRef"
              placeholder="0123 456 789"
              :class="[
              'pl-10 h-12 text-base w-full rounded-md border-input bg-background py-2 px-3 border-2 focus:border-blue-500 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-blue-100 focus:outline-none',
              errors.phone && 'border-red-500 focus:ring-red-500'
            ]"
              v-model="phoneNumber"
              @input="handlePhoneChange"
              maxlength="12"
          />
        </div>
        <p v-if="errors.phone" class="text-red-500 text-base mt-1 font-semibold leading-5">{{ errors.phone }}</p>
      </div>

      <button
          type="submit"
          class="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:pointer-events-none"
          :disabled="isLoading"
      >
        {{ isLoading ? "Đang gửi OTP..." : "Lấy OTP" }}
      </button>
    </div>

    <!-- Step 3: OTP Verification -->
    <div v-if="currentStep === 3" class="space-y-4">
      <div class="space-y-2">
        <label for="otp" class="block text-base font-medium text-gray-500">
          Mã xác thực OTP <span class="text-red-500 text-base font-bold">*</span>
        </label>
        <div class="relative">
          <Lock class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"/>
          <input
              id="otp"
              type="text"
              ref="otpInputRef"
              placeholder="123456"
              :class="[
              'pl-10 h-12 text-base w-full rounded-md border-input bg-background py-2 px-3 border-2 focus:border-blue-500 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-blue-100 focus:outline-none',
              errors.otp && 'border-red-500 focus:ring-red-500'
            ]"
              v-model="otp"
              @input="handleOtpChange"
              maxlength="6"
          />
        </div>
        <p v-if="errors.otp" class="text-red-500 text-base mt-1 font-semibold leading-5">{{ errors.otp }}</p>
        <p class="text-sm text-gray-500 mt-2" v-show="isShowHint">
          Mã xác thực 6 số đã được gửi đến số điện thoại của bạn. Vui lòng kiểm tra Zalo/Tin nhắn để lấy mã.
        </p>
        <p class="text-sm text-gray-500 mt-2" v-show="isShowHint">
          Mã xác thực có hiệu lực trong 5 phút.
        </p>
      </div>

      <button
          type="submit"
          class="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:pointer-events-none"
          :disabled="isLoading"
      >
        {{ isLoading ? "Đang xác thực..." : "Xác thực" }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import {nextTick, onMounted, ref, watch, type Ref} from 'vue';
import {Mail, Phone, Lock} from 'lucide-vue-next';
import {useRouter} from 'vue-router';
import {useAuthStore} from "@/stores/auth.js";
import authRequests from "@/utils/authRequests.ts";
import {errorMsg} from "@/i18n/i18nCommon.tsx";
import {toast} from "vue3-toastify";
import {errorCodes} from "@/i18n/i18nEnum.tsx";

const props = defineProps({
  currentStep: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['nextStep']);

// Refs for elements
const emailInputRef: Ref<any> = ref(null);
const phoneInputRef: Ref<any> = ref(null);
const otpInputRef: Ref<any> = ref(null);

const auth = useAuthStore()
const router = useRouter()

// Form states
const email: Ref<any> = ref(auth.email);
const phoneNumber: Ref<any> = ref(auth.phone);

const otp: Ref<any> = ref('');
const errors: Ref<any> = ref({});
const isLoading: Ref<boolean> = ref(false);
const isShowHint: Ref<boolean> = ref(true);
const msg: Ref<any> = ref(null);

onMounted(() => {
  if (props.currentStep === 1) {
    emailInputRef.value?.focus();
  }

  if (localStorage.getItem('email')) {
    email.value = localStorage.getItem('email')
  }
})

// Focus on inputs when a step changes
watch(() => props.currentStep, (newStep) => {
  nextTick(() => {
    if (newStep === 2 && phoneInputRef) {
      phoneInputRef.value.focus();
    } else if (newStep === 3 && otpInputRef) {
      otpInputRef.value.focus();
    }
  });
});

// Validation functions
const validateEmail = (email: any) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) {
    errors.value = {...errors.value, email: 'Vui lòng nhập Email để tiếp tục!'};
    return false;
  }
  if (!emailRegex.test(email)) {
    errors.value = {...errors.value, email: 'Email không đúng định dạng!'};
    return false;
  }
  errors.value = {...errors.value, email: ''};
  return true;
};

const validatePhone = (phone: any) => {
  const phoneRegex = /^(0|\+84)[3|5|7|8|9][0-9]{8}$/;
  if (!phone) {
    errors.value = {...errors.value, phone: 'Vui lòng nhập số điện thoại (10 số) để tiếp tục!'};
    return false;
  }
  if (!phoneRegex.test(phone.replace(/\D/g, ''))) {
    errors.value = {...errors.value, phone: 'Số điện thoại không đúng định dạng!'};
    return false;
  }
  errors.value = {...errors.value, phone: ''};
  return true;
};

const validateOtp = (otp: any) => {
  const otpRegex = /^\d{6}$/;
  if (!otp) {
    errors.value = {...errors.value, otp: 'Vui lòng nhập mã OTP để tiếp tục!'};
    return false;
  }
  if (!otpRegex.test(otp)) {
    errors.value = {...errors.value, otp: 'Mã OTP không đúng định dạng'};
    return false;
  }
  errors.value = {...errors.value, otp: ''};
  return true;
};

// Handle form submission
const handleSubmit = async () => {
  isLoading.value = true;

  try {
    if (props.currentStep === 1) {
      if (validateEmail(email.value)) {
        auth.email = email.value
        localStorage.setItem('email', email.value)

        try {
          const {data: validate} = await authRequests.requestEmail(email.value)

          if (validate.IsExist) {
            toast.success("Xác thực Email thành công!")

            auth.user = validate.User
            auth.minigame = validate.User.Minigame

            if (validate.IsVerified) {
              auth.isAuthenticated = true
              await router.push('/')
            } else {
              phoneNumber.value = validate.User.Phone
              auth.phone = validate.User.Phone
              msg.value = validate.Msg
              emit('nextStep', validate.NextStep);
            }
          } else {
            errors.value = {...errors.value, email: validate.Msg};
          }
        } catch (error: any) {
          if (error) {
            if (error.status === errorCodes.DuplicateCode) {
              errors.value = {...errors.value, email: error.errors?.Phone?.join('\n')};
            } else {
              errors.value = {...errors.value, email: error.errors?.Email?.join('\n')};
            }
          } else {
            errors.value = {
              ...errors.value,
              email: errorMsg.Error
            };
          }
        }
      }
    } else if (props.currentStep === 2) {
      if (validatePhone(phoneNumber.value)) {
        auth.phone = phoneNumber.value.replace(/\D/g, '')

        try {
          const {data: sendOtp} = await authRequests.requestPhone(auth.phone, auth.user?.UserId)

          if (sendOtp.IsSuccess) {
            toast.success("Xác thực Số điện thoại thành công!")
            msg.value = null;
            emit('nextStep', sendOtp.NextStep);
          } else {
            errors.value = {...errors.value, phone: "Không gửi được mã xác thực tới SĐT này!"};
          }
        } catch (error: any) {
          if (error) {
            errors.value = {...errors.value, phone: error.errors?.Phone?.join('\n')};
          } else {
            errors.value = {
              ...errors.value,
              phone: errorMsg.Error
            };
          }
        }
      }
    } else if (props.currentStep === 3) {
      if (validateOtp(otp.value)) {
        try {
          isShowHint.value = false;

          const {data: res} = await authRequests.verifyOTP(otp.value, auth.phone)
          toast.success("Xác thực OTP thành công!")

          auth.user = res.User
          auth.minigame = res.User.Minigame

          auth.isAuthenticated = true
          await router.push('/')
        } catch (error: any) {
          if (error) {
            errors.value = {...errors.value, otp: error.errors?.OTP?.join('\n')};
          } else {
            errors.value = {
              ...errors.value,
              otp: errorMsg.Error
            };
          }
        }

        await router.push('/');
      }
    }
  } catch (error) {
    toast.error(errorMsg.Error)
  } finally {
    isLoading.value = false;
  }
};

// Format phone number
const formatPhoneNumber = (value: any) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 4) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 4)} ${numbers.slice(4)}`;
  return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7, 10)}`;
};

// Handle phone input
const handlePhoneChange = (e: any) => {
  phoneNumber.value = formatPhoneNumber(e.target.value);
  validatePhone(e.target.value);
};

// Handle OTP input
const handleOtpChange = (e: any) => {
  const value = e.target.value.replace(/\D/g, '');
  if (value.length <= 6) {
    otp.value = value;
  }
};
</script> 